(ns bleene.dag.core
  (:require
   [clojure.set]
   [clojure.string]
   [react :as react]
   [applied-science.js-interop :as jsi]
   [re-posh.core :as re-posh]
   [konva :as Konva]
   [ui.chip]
   [ui.button :refer [button]]
   [ui.fields :refer [select input]]
   [ui.dropdown-menu :refer [dropdown-menu dropdown-menu-item
                             dropdown-menu-sub dropdown-menu-checkbox]]
   [react-konva :refer [Stage Layer]]
   [react-hotkeys-hook :refer [useHotkeys]]
   [react-resize-detector :refer [useResizeDetector]]
   [re-frame.core :as re-frame]
   [goog.functions :refer [debounce throttle]]
   [minorodata.utils :refer [find-by-field]]
   [bleene.dag.events]
   [bleene.dag.api]
   [bleene.dag.subs]
   [bleene.dag.minimap :as minimap]
   [bleene.dag.context-menu :as context-menu]
   [bleene.dag.nodes :as nodes]
   [bleene.dag.connections :as connections]
   [bleene.dag.dag-context :refer [Provider]]
   [bleene.utils :as utils]
   [bleene.dag.commons :as commons]
   [reagent.core :as reagent]
   ["lucide-react" :refer [Save Workflow DownloadCloud X ChevronDown ChevronRight Check]]
   [ui.dialog]
   [ui.button]
   ["./dag" :default dag-ng]))


; (jsi/assoc! :pixelRatio Konva 1)

(defn add-node-at-event-position [event dag-id]
  (let [mouse-point (utils/event->position event)]
    (re-frame/dispatch [:dag/add-node dag-id mouse-point])
    (re-frame/dispatch [:layout/open-dialog :selected-node {:dag-id dag-id}])))


(def persist-scroll-updates
  (debounce
   (fn [stage dag-id new-scale new-pos]
     (re-frame/dispatch [:dag/open-context-menu false])
     (re-frame/dispatch [:dag/set-position dag-id new-pos])
     (re-frame/dispatch-sync [:dag/set-scale dag-id new-scale])
     (jsi/call stage :fire "x-scale-changed"))
   50))


(def unmute-events
  (debounce
   (fn [dag-id]
     (re-frame/dispatch [:dag/mute-events dag-id false]))
   100))


(def mute-events
  (throttle
   (fn [dag-id]
     (re-frame/dispatch [:dag/mute-events dag-id true]))
   100))


(defn update-group-titles
  "Updates title font size so that text stays the same size regardless of scale"
  [stage new-scale]
  (let [scale  (if (<= 1 new-scale) 1 (/ 1 new-scale))
        titles (jsi/call stage :find ".nodes-group-title")]
    (doseq [title titles]
      (jsi/call title :scale #js {:x scale :y scale}))))


(defn handle-scroll [event {:keys [dag-id scale-by min-scale max-scale]}]
  (jsi/call-in event [:evt :preventDefault])
  (mute-events dag-id)

  (let [target         (jsi/get event :target)
        stage          (jsi/call target :getStage)
        delta          (jsi/get-in event [:evt :deltaY])
        {:keys [point-x point-y scale]} (utils/event->points event)
        mouse-point-to (utils/event->position event)
        new-scale      (if (> delta 0)
                         (/ scale scale-by)
                         (* scale scale-by))
        new-pos        {:x (- point-x (* (:x mouse-point-to) new-scale))
                        :y (- point-y (* (:y mouse-point-to) new-scale))}]
    (when (< min-scale new-scale max-scale)
      (jsi/call stage :scale #js {:x new-scale :y new-scale})
      (jsi/call stage :position (clj->js new-pos))
      (persist-scroll-updates stage dag-id new-scale new-pos))

    (unmute-events dag-id)))


(defn handle-drag-start [event]
  (let [target    (jsi/get event :target)
        stage     (jsi/call target :getStage)
        container (jsi/call stage :container)]
    (jsi/assoc-in! container [:style :cursor] "grabbing")
    (re-frame/dispatch [:dag/open-context-menu false])))


(defn handle-drag-end [event dag-id]
  (let [target    (jsi/get event :target)
        stage     (jsi/call target :getStage)
        container (jsi/call stage :container)
        x         (jsi/call stage :x)
        y         (jsi/call stage :y)]
    (jsi/assoc-in! container [:style :cursor] "default")
    (re-frame/dispatch [:dag/set-position dag-id {:x         x
                                                  :y         y
                                                  :minimap-x x
                                                  :minimap-y y}])))


(defn handle-drag-move [event dag-id]
  (let [target   (jsi/get event :target)
        stage    (jsi/call target :getStage)
        top-left (jsi/call stage :absolutePosition)
        offset-x (jsi/get top-left :x)
        offset-y (jsi/get top-left :y)
        type     (jsi/get target :nodeType)]
    (when (= type "Stage")
      (re-frame/dispatch-sync [:dag/set-offset dag-id {:offset-x offset-x :offset-y offset-y}]))
    ;; propagate event to next listeners
    true))


(defn open-stage-context [event]
  (commons/open-context-menu event {:type :stage}))


(defn dag [{:keys [stage-ref dag-id container]}]
  (let [{:keys [x y navigate-to node-width node-height
                scale scale-by min-scale max-scale]
         :or   {x 0 y 0 scale 1}}
        (utils/query {:x           [:dag dag-id :x]
                      :y           [:dag dag-id :y]
                      :scale       [:dag dag-id :scale]
                      :navigate-to [:dag dag-id :navigate-to]
                      :node-width  [:dag-params :node-width]
                      :node-height [:dag-params :node-height]
                      :scale-by    [:dag-params :scale-by]
                      :min-scale   [:dag-params :min-scale]
                      :max-scale   [:dag-params :max-scale]})]

    ;; (useHotkeys "Backspace" #(re-frame/dispatch [:dag/delete-elements dag-id]))
    (useHotkeys "Escape" #(re-frame/dispatch [:dag/cleanup-selected dag-id]))

    (react/useEffect
     (fn []
       (when (some? navigate-to)

         (let [{:keys [x y]} navigate-to
               half-screen-width  (/ (jsi/get container :width) 2)
               half-screen-height (/ (jsi/get container :height) 2)
               half-node-width    (/ node-width 2)
               half-node-height   (/ node-height 2)
               to-x               (- (+ (- x) half-screen-width) half-node-width)
               to-y               (- (+ (- y) half-screen-height) half-node-height)
               reset-navigation   (fn []
                                    (re-frame/dispatch [:dag/reset-navigation dag-id])
                                    (re-frame/dispatch [:dag/set-scale dag-id 1])
                                    (re-frame/dispatch [:dag/set-position dag-id {:x to-x :y to-y}]))
               tween-props        #js {:x        to-x
                                       :y        to-y
                                       :scaleX   1
                                       :scaleY   1
                                       :duration 0.2
                                       :onFinish reset-navigation}]
           (jsi/call-in stage-ref [:current :to] tween-props)))

       (fn []))
     #js [navigate-to])

    [:> Stage
     {:ref            stage-ref
      :x              x
      :y              y
      :scale          {:x scale :y scale}
      :width          (jsi/get container :width)
      :height         (jsi/get container :height)
      :draggable      true

      :on-wheel       (fn handle-wheel [evt]
                        (handle-scroll evt {:dag-id    dag-id
                                            :scale-by  scale-by
                                            :min-scale min-scale
                                            :max-scale max-scale}))
      :on-contextmenu (fn handle-context-menu [evt]
                        (open-stage-context evt))}

     ;; react-konva Stage component doesn't work with react context,
     ;; so we need a second internal provider inside the Stage
     ;; https://github.com/konvajs/react-konva/issues/188
     [:> Provider {:value #js {:dag-id dag-id}}
      ;; Shapes
      [:> Layer {:name "groups"}
       [:f> nodes/groups]]

      [:> Layer {:name "connections"}
       [:f> connections/connections]]

      [:> Layer {:name "nodes"}
       [:f> nodes/nodes
        {:stage-ref stage-ref}]]]]))



(defn simple-query [db query-map]
  (reduce-kv (fn [m k v]
               (assoc m k (get-in db v)))
             {}
             query-map))

(defn use-run-once [{:keys [deps condition fun]}]
  (let [init-ref (react/useRef false)]
    (react/useEffect
     (fn []
       (when (and (not (jsi/get init-ref :current))
                  (condition))
         (fun)
         (jsi/assoc! init-ref .-current true))
       (fn []))
     (clj->js deps))))


(defn dag-native' [{:keys [stage-ref dag-id dag-data container query node-data statuses]}]
  (let [{:keys [navigate-to node-width node-height loading?]} query
        ts (:ts node-data)

        height (jsi/get container :height)
        width  (jsi/get container :width)

        container-id (str  "dag-container-" dag-id)]

    (useHotkeys "Escape" #(re-frame/dispatch [:dag/cleanup-selected dag-id]))

    (react/useEffect
     (fn []
       (let [{:keys [x y]} navigate-to
             half-screen-width  (/ (jsi/get container :width) 2)
             half-screen-height (/ (jsi/get container :height) 2)
             half-node-width    (/ node-width 2)
             half-node-height   (/ node-height 2)
             to-x               (- (+ (- x) half-screen-width) half-node-width)
             to-y               (- (+ (- y) half-screen-height) half-node-height)
             reset-navigation   (fn []
                                  (re-frame/dispatch [:dag/reset-navigation dag-id])
                                  (re-frame/dispatch [:dag/set-scale dag-id 1])
                                  (re-frame/dispatch [:dag/set-position dag-id {:x to-x :y to-y}]))
             tween-props        #js {:x        to-x
                                     :y        to-y
                                     :scaleX   1
                                     :scaleY   1
                                     :duration 0.2
                                     :onFinish reset-navigation}]
         (when (some? navigate-to)
           (.tween dag-ng container-id tween-props))

         (fn [])))
     #js [navigate-to])

    (react/useEffect
     (fn []
       (.dag dag-ng container-id dag-data #js {:height height :width width})

       (fn cleanup []
         ; (jsi/call dag-ng :deleteDag container-id)
         ))
     #js [container-id ts node-data])

    (use-run-once
     {:deps      [statuses loading?]
      :condition #(and (seq statuses) (not loading?))
      :fun       #(.dag dag-ng container-id dag-data #js {:height height :width width})})

    ^{:key (str container-id loading?)}
    [:div {:id     container-id
           :style {:background-color "transparent"
                   :visibility (if (or (not loading?) (:ts node-data)) "visible" "hidden")
                   :width  "100%"
                   :height "100%"
                   :position "relative"}}]))


(defn dag-native [{:keys [dag-id] :as props}]
  (let [query (utils/query {:loading?    [:loader :loading?]
                            :navigate-to [:dag dag-id :navigate-to]
                            :node-width  [:dag-params :node-width]
                            :node-height [:dag-params :node-height]})

        dag-data  @(re-posh/subscribe [:dag/dag-data dag-id])
        node-data @(re-posh/subscribe [:dag/node-ids-for-dag dag-id])
        statuses  @(re-posh/subscribe [:transform-status-list/prop :transform-status-list/statuses])]
    [:f> dag-native' (assoc props
                            :dag-data  dag-data
                            :query     query
                            :node-data node-data
                            :statuses  statuses)]))

(def search-panel-deps-options
  [{:value "all" :text "All"}
   {:value "upstream" :text "Upstream"}
   {:value "downstream" :text "Downstream"}])


(def search-panel-deps-labels
  {"all"        "All"
   "upstream"   "Upstream"
   "downstream" "Downstream"})

(defn stringify-keys [obj]
  (->> obj
       (map (fn [[k v]] [(name k) v]))
       (into {})))

(defn save-layout [dag-id]
  (let [new-layout-js  (.getLayout dag-ng (str "dag-container-" dag-id))
        new-layout     (js->clj new-layout-js :keywordize-keys true)
        jobs           (stringify-keys (:nodes new-layout))
        connections    (stringify-keys (:connections new-layout))]
    (re-frame/dispatch [:dag/persist-layout dag-id {:jobs jobs :connections connections}])
    (re-frame/dispatch [:dag.api/fetch-available-dag-layouts])))

(defn auto-layout [dag-id]
  (re-frame/dispatch [:dag/reset-layout dag-id])
  (re-frame/dispatch [:dag/full-autolayout dag-id]))

(defn autolayout-confirm-dialog
  [{:keys [dag-id show on-close]}]
  [ui.dialog/dialog {:open show
                     :on-open-change on-close
                     :content-props {:size :small}
                     :title "Auto Layout"
                     :content [:div
                               [:p "You are about to rearrange the Pipelines, this cannot be undone. Proceed?"]
                               [:div {:class "flex gap-2 mt-4 justify-end"}
                                [ui.button/button {:variant :empty
                                            :on-click on-close}
                                 "Cancel"]
                                [ui.button/button {:on-click (fn []
                                                               (auto-layout dag-id)
                                                               (on-close))}
                                 "Confirm"]]]}])

(defn save-layout-confirm-dialog
  [{:keys [dag-id show on-close]}]

  [ui.dialog/dialog {:open show
                     :on-open-change on-close
                     :content-props {:size :small}
                     :title "Save layout"
                     :content [:div
                               [:p
                                "You cannot save to another user’s account, do you want to overwrite your Pipelines layout?"]
                               [:div {:class "flex gap-2 mt-4 justify-end"}
                                [ui.button/button {:variant :empty
                                                   :on-click on-close}
                                 "Cancel"]
                                [ui.button/button {:on-click (fn []
                                                               (save-layout dag-id)
                                                               (on-close))}
                                 "Confirm"]]]}])

(defn refresh-dag [dag-id]
  (let [{:keys [nodes]} (utils/query {:nodes [:dag dag-id {:nodes [:jobs]}]})
        ids      (map #(or (get-in % [:transform :master-id])
                           (get-in % [:extract :master-id]))
                             nodes)]
    (re-frame/dispatch [:connections.api/fetch-transform-statuses {:ids ids}])
    (re-frame/dispatch-sync [:dag/load-dag-data dag-id])))

(defn search-panel [{:keys [dag-id]}]
  (let [show-dialog?         (reagent/atom false)
        show-confirm-dialog  #(reset! show-dialog? true)
        close-confirm-dialog #(reset! show-dialog? false)

        user    @(re-posh/subscribe [:user/pull])
        user-id (:user/uid user)
        selected-user  (reagent/atom user-id)

        show-save-dialog? (reagent/atom false)

        node-search  (reagent/atom "")
        group-search (reagent/atom "")
        tag-search (reagent/atom "")

        show-save-dialog  (fn save-layout-dialog-toggle []
                            (let [layout-origin @(re-posh/subscribe [:dag/current-layout-origin])
                                  saving-own-layout? (or (= layout-origin user-id)
                                                         (nil? layout-origin))]

                              (if saving-own-layout?
                                (save-layout dag-id)
                                (reset! show-save-dialog? true))))

        close-save-dialog #(reset! show-save-dialog? false)]
    (fn []
      (let [{:keys [jobs filters show-sources? show-all-extracts? group-details]}
            (utils/query {:jobs               [:jobs]
                          :show-sources?      [:dag dag-id :show-sources?]
                          :show-all-extracts? [:dag dag-id :show-all-extracts?]
                          :filters            [:dag dag-id :filters]
                          :group-details      [:groups]})


            {:keys [node dir group tags] :or {dir "all" tags #{}}} filters

            job-options-to-display (cond->> (vals jobs)
                                     (some? group) (filter (comp (partial = group) :group-id)))

            jobs-options (mapv (fn [{:keys [id name]}]
                                 {:value id :label name})
                               job-options-to-display)

            group-ids (keys group-details)

            groups-options (sort-by (comp clojure.string/lower-case :label)
                                    (mapv (fn [id]
                                            {:value id :label (or (get-in group-details [id :name] "") "**EMPTY NAME**")})
                                          group-ids))


            all-tags       (->> jobs
                                vals
                                (map (comp :tags :transform))
                                flatten
                                distinct
                                (filter some?)
                                (sort-by clojure.string/lower-case))

            user-dag-options    (re-posh/subscribe [:dag/available-dag-layout-options])

            show-manual-layout? @(re-posh/subscribe [:unleash/is-enabled "fe.manual-dag-layout"])
            layout-select?      @(re-posh/subscribe [:unleash/is-enabled "fe.dag-user-select"])

            default-dag?        (= dag-id :default-dag)]

        [:div {:class ["absolute top-3 left-3 inline-flex flex-wrap gap-2 p-1 mr-40"
                       "bg-white dark:bg-kleene-bg-dark"
                       "border border-kleene-neutral-02 dark:border-kleene-dark-02 rounded-lg"]}

         ;; Groups select
         [:div {}
          [dropdown-menu {:props {:align "start"}
                          :content [:div {:class "relative"}

                                    [input {:class "sticky top-0 z-10 mb-2"
                                            :auto-focus true
                                            :placeholder "Filter..."
                                            :field {:value  @group-search
                                                    :action (fn [evt]
                                                              (jsi/call evt :stopPropagation)
                                                              (jsi/call evt :preventDefault)

                                                              (reset! group-search (jsi/get-in evt [:target :value])))}}]

                                    (into [:<>]
                                          (for [option groups-options]
                                            (let [empty-search? (clojure.string/blank? @group-search)
                                                  label (or (:label option) "")
                                                  matched? (and (not empty-search?)
                                                                (->> [label @group-search]
                                                                     (map clojure.string/lower-case)
                                                                     (apply clojure.string/includes?)))]
                                              [dropdown-menu-item
                                               {:textValue ""
                                                :active (and (some? group)
                                                             (= group
                                                                (:value option)))
                                                :class [(when (and (not empty-search?)
                                                                   (not matched?))
                                                          "hidden!")]
                                                :on-click (fn []
                                                            (re-frame/dispatch [:dag/set-filter-group dag-id (:value option)]))}
                                               (:label option)])))]}

           [:div
            [button {:class ""
                     :variant :empty
                     :icon-right (if (some? group)
                                   X
                                   ChevronDown)
                     :icon-right-props (when (some? group)
                                         {:title "Show all jobs"
                                          :class "pointer-events-auto"
                                          :on-click (fn [evt]
                                                      (jsi/call evt :stopPropagation)
                                                      (jsi/call evt :preventDefault)
                                                      ; (re-frame/dispatch [:dag/set-filter-group dag-id nil])
                                                      (re-frame/dispatch [:dag/clear-filter dag-id]))})}
             [:div {:class "w-40 truncate text-left"}
              (get (find-by-field groups-options :value group)
                   :label
                   "Groups")]]]]]


         [:div {:class ""}
          [dropdown-menu {:props {:align "start"}
                          :content [:div {:class "relative"}

                                    [input {:class "sticky top-0 z-10 mb-2"
                                            :placeholder "Filter..."
                                            :auto-focus true
                                            :field {:value  @node-search
                                                    :action (fn [evt]
                                                              (jsi/call evt :stopPropagation)
                                                              (jsi/call evt :preventDefault)

                                                              (reset! node-search (jsi/get-in evt [:target :value])))}}]

                                    (into [:<>]
                                          (for [option jobs-options]
                                            (let [empty-search? (clojure.string/blank? @node-search)
                                                  label (or (:label option) "")
                                                  matched? (and (not empty-search?)
                                                                (->> [label @node-search]
                                                                     (map clojure.string/lower-case)
                                                                     (apply clojure.string/includes?)))]
                                              [dropdown-menu-item
                                               {:textValue ""
                                                :active (and (some? node)
                                                             (= node
                                                                (:value option)))
                                                :class [(when (and (not empty-search?)
                                                                   (not matched?))
                                                          "hidden!")]
                                                :on-click (fn []
                                                            (re-frame/dispatch [:dag/filter-nodes dag-id (:value option)]))}
                                               (:label option)])))]}

           [:div
            [button {:class ""
                     :variant :empty
                     :icon-right (if (some? node)
                                   X
                                   ChevronDown)
                     :icon-right-props (when (some? node)
                                         {:title "Show all jobs"
                                          :class "pointer-events-auto"
                                          :on-click (fn [evt]
                                                      (jsi/call evt :stopPropagation)
                                                      (jsi/call evt :preventDefault)
                                                      (re-frame/dispatch [:dag/clear-filter dag-id]))})}
             [:div {:class "w-60 truncate text-left overflow-hidden text-ellipsis rtl text-left"
                    :dir   "rtl"}
              (get (find-by-field jobs-options :value node)
                   :label
                   "Sources and transforms")]]]]]


         [:div {:class ""}
          [select {:field {:value dir
                           :action (fn [direction]
                                     (re-frame/dispatch [:dag/set-filter-dir dag-id direction]))}
                   :disabled (not (some? node))
                   :errors nil
                   :id :direction-select
                   :variant :empty
                   :label nil
                   :native false
                   :field-props {:class "font-medium"}
                   :options search-panel-deps-options}]]



         ;; Tags filter
         [:div {}
          [dropdown-menu {:props {:align "start"}
                          :content [:div {:class "relative"}

                                    [input {:class "sticky top-0 z-10 mb-2"
                                            :auto-focus true
                                            :placeholder "Filter..."
                                            :field {:value  @tag-search
                                                    :action (fn [evt]
                                                              (jsi/call evt :stopPropagation)
                                                              (jsi/call evt :preventDefault)

                                                              (reset! tag-search (jsi/get-in evt [:target :value])))}}]

                                    (into [:<>]
                                          (for [tag all-tags]
                                            (let [empty-search? (clojure.string/blank? @tag-search)
                                                  label (or tag "")
                                                  matched? (and (not empty-search?)
                                                                (->> [label @tag-search]
                                                                     (map clojure.string/lower-case)
                                                                     (apply clojure.string/includes?)))
                                                  currently-filtered (some #{tag} tags)]
                                              [dropdown-menu-item
                                               {:textValue ""
                                                :active (and (some? tag)
                                                             (some #{tag} tags))
                                                :class [(when (and (not empty-search?)
                                                                   (not matched?))
                                                          "hidden!")]
                                                :on-click (fn [evt]

                                                            (jsi/call evt :stopPropagation)
                                                            (jsi/call evt :preventDefault)
                                                            (re-frame/dispatch [:dag/set-filter-tags dag-id (if currently-filtered
                                                                                                              (disj (set tags) tag)
                                                                                                              (conj (set tags) tag))]))}
                                               tag])))]}

           [:div
            [button {:class "mr-3"
                     :variant :empty
                     :icon-right (if (empty? tags)
                                   ChevronDown
                                   X)
                     :icon-right-props (when-not (empty? tags)
                                         {:title "Clear tags filter"
                                          :class "pointer-events-auto"
                                          :on-click (fn [evt]
                                                      (jsi/call evt :stopPropagation)
                                                      (jsi/call evt :preventDefault)
                                                      (re-frame/dispatch [:dag/set-filter-tags dag-id #{}])
                                                      #_(re-frame/dispatch [:dag/clear-filter dag-id]))})}
             [:div {:class "min-w-20 truncate text-left"}
              "Tags"
              (when-not (empty? tags)
                (str " (" (count tags) " selected)"))]]]]]


         (when (and show-manual-layout? default-dag?)
           [dropdown-menu {:content [:div
                                     (when (and @user-dag-options layout-select?)
                                       [dropdown-menu-sub {:content
                                                           (into
                                                            [:<>]
                                                            (map (fn [{:keys [value text]}]
                                                                   (let [layout-origin @(re-posh/subscribe [:dag/current-layout-origin])
                                                                         selected-layout-id (or layout-origin user-id)]
                                                                     [dropdown-menu-checkbox
                                                                      {:icon Check
                                                                       :checked (= selected-layout-id value)
                                                                       :on-click (fn []
                                                                                   (re-frame/dispatch [:dag/preview-layout dag-id value]))}
                                                                      text]))
                                                                 @user-dag-options))}
                                        [:span {:class "flex items-center gap-2 justify-between w-full pl-5"}
                                         [:span "Open"]
                                         [:> ChevronRight {:size 16}]]])

                                     [dropdown-menu-checkbox
                                      {:icon Check
                                       :class "pr-7"
                                       :checked show-sources?
                                       :on-click (fn []
                                                   (re-frame/dispatch [:dag/set-sources-on-dag dag-id (not show-sources?)]))}
                                      "Show sources"]

                                     [dropdown-menu-checkbox
                                      {:icon Check
                                       :class "pr-7"
                                       :disabled (not show-sources?)
                                       :checked  show-all-extracts?
                                       :on-click (fn []
                                                   (re-frame/dispatch [:dag/set-show-all-extracts dag-id (not show-all-extracts?)]))}
                                      "Show all extracts"]

                                     [dropdown-menu-item
                                      {:class "px-7"
                                       :on-click show-save-dialog}
                                      "Save"]

                                     [dropdown-menu-item
                                      {:class "px-7"
                                       :on-click show-confirm-dialog}
                                      "Auto layout"]]}
            [:div
             [button {:variant :empty
                      :icon-right ChevronDown
                      :class ""}
              "Layout"]]])


         [save-layout-confirm-dialog {:dag-id dag-id
                                      :show @show-save-dialog?
                                      :on-close close-save-dialog}]

         [autolayout-confirm-dialog {:dag-id   dag-id
                                     :show     @show-dialog?
                                     :on-close close-confirm-dialog}]

         (when-not (empty? tags)
           [:div {:class "grow w-full flex flex-row flex-wrap gap-2 p-1"}
            [:span {} "Filtered tags: "]
            (into [:<>]
                  (map (fn [tag]
                         [ui.chip/chip {:on-delete (fn delete-tag []
                                                     (re-frame/dispatch [:dag/set-filter-tags dag-id (disj (set tags) tag)]))}
                          tag])
                       tags))])]))))


(defn loader []
  (let [{{:keys [loading? message]} :loader} (utils/query {:loader [:loader]})]
    (when loading?
      [:div
       {:class "absolute left-7 bottom-7 text-white py-3 px-4 rounded-lg bg-kleene-green"}
       (str message "...")])))


(def TRANSFORM_STATUS_FETCH_INTERVAL 10000)

(defn dag-ui [{:keys [dag-id]}]
  (let [container (useResizeDetector)
        stage-ref (react/useRef nil)

        {:keys [nodes]} (utils/query {:nodes [:dag dag-id {:nodes [:jobs]}]})
        master-ids      (map #(or (get-in % [:transform :master-id])
                                  (get-in % [:extract :master-id]))
                             nodes)

        status-fetcher (fn status-fetcher [ids]
                         (let [loading-statuses? @(re-posh/subscribe [:mold-http/loading? :connections.api/fetch-transform-statuses])]

                           (when (and (not loading-statuses?) ;; skip fetching statuses if previous request hasn't finished yet
                                      (not-empty ids)) ;; don't bother requesting statuses if no nodes are on the dag
                             (re-frame/dispatch [:connections.api/fetch-transform-statuses {:ids ids}]))))

        route          (re-frame/subscribe [:app-route/pull])

        initial-focus-id (some-> @route :app-route/query :focus)]

    ;; Set up keyboard listener
    (useHotkeys "alt+s" #(re-frame/dispatch [:dag/persist-layout]))

    (react/useEffect
     (fn []
       (let [{:keys [raw-data]} (utils/query {:raw-data [:raw-data]})]

         (re-frame/dispatch [:users.api/fetch-searchable-users])
         (re-frame/dispatch [:dag.api/fetch-available-dag-layouts])

         (if raw-data
           (when initial-focus-id
             (re-frame/dispatch [:dag/navigate-to-node dag-id initial-focus-id]))
           (do
             (re-frame/dispatch-sync [:transformations-search/add])
             (re-frame/dispatch [:dag/load-dag-data dag-id initial-focus-id])
             (re-frame/dispatch [:connections.api/fetch-user-connectors])
             (re-frame/dispatch [:connections.events/load-data]))))

       (fn []))
     #js [dag-id])


    (react/useEffect
     (fn []
       (let [{:keys [raw-data]} (utils/query {:raw-data [:raw-data]})]
         (when (and raw-data initial-focus-id)
           (re-frame/dispatch [:dag/navigate-to-node dag-id initial-focus-id])))

       (fn []))
     #js [initial-focus-id])


    (react/useEffect
     (fn []
       (status-fetcher master-ids)

       (let [fetcher (js/setInterval #(status-fetcher master-ids) TRANSFORM_STATUS_FETCH_INTERVAL)]

         (fn []
           (js/clearInterval fetcher))))
     #js [master-ids])

    [:> Provider {:value #js {:dag-id dag-id}}
     ;; Wrapping container
     [:div
      {:class "h-full bg-white dark:bg-slate-900"
       :ref   (jsi/get container :ref)}

      ;; Background with Dots
      ; [:f> background/background
      ;  {:gap 50 :size 2 :type :dots}]

      ;; Main DAG stage
      [:f> dag-native {:stage-ref stage-ref
                       :dag-id    dag-id
                       :container container}]

      ;; Minimap
      [:f> minimap/minimap
       {:stage-ref stage-ref
        :container container}]

      ;; Context menu
      [:f> context-menu/context-menu
       {:stage-ref stage-ref}]

      [:f> nodes/node-tooltip]

      [search-panel {:dag-id dag-id}]

      [loader]]]))
